import PropTypes from 'prop-types'
import { Pencil } from 'lucide-react'

import { productCategories } from '@components/product'
import { SortIndicator } from '@components/sort-indicator'
import { AdasLink, AdasTypography } from '@components/wrapper-components'
import { SupportStatusInfo } from '@components/status-info'
import { ccidFormatter, currencyFormatter, formatDateTime, getRole } from 'utils/utils'
import { DATE_FORMATS } from '../../constants'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table'
import { cn } from 'lib/utils'

const renderers = {
  name: ({ content, onSelect }) => (
    <AdasLink
      href='#'
      underline='none'
      onClick={(e) => {
        e.preventDefault() //Added this to removing # after each route
        onSelect(content, 'name')
      }}
    >
      {content.name}
    </AdasLink>
  ),
  is_active: ({ content }) =>
    // Users only: an active account whose set-up link is still outstanding (invited, no
    // password yet). Other tables never carry setup_pending.
    content.is_active && content.setup_pending ? (
      <span className='inline-flex items-center justify-center rounded-full bg-[#FEF3C7] px-3 py-1 text-[12px] font-semibold text-[#92400E]'>
        Pending
      </span>
    ) : content.is_active ? (
      <span className='inline-flex items-center justify-center rounded-full bg-[#DCFCE7] px-3 py-1 text-[12px] font-semibold text-[#16803F]'>
        Active
      </span>
    ) : (
      <span className='inline-flex items-center justify-center rounded-full bg-[#EBEDF0] px-3 py-1 text-[12px] font-semibold text-[#6B7280]'>
        Inactive
      </span>
    ),
  price: ({ content }) => currencyFormatter(content.price),
  category: ({ content }) => productCategories[content.category] || content.category.name,
  id: ({ content }) => ccidFormatter(content.id),
  ccid: ({ content }) => content.ccid,
  center: ({ content, onSelect }) =>
    content.center ? (
      <AdasLink href='#' underline='none' onClick={() => onSelect(content, 'center')}>
        {content.center}
      </AdasLink>
    ) : (
      '-'
    ),
  opening_date: ({ content }) => (content.opening_date ? new Date(content.opening_date).toLocaleDateString() : '-'),
  created_at: ({ content }) => (content.created_at ? formatDateTime(new Date(content.created_at), DATE_FORMATS.ISO_DATE_STRING) : '-'),
  updated_at: ({ content }) => (content.updated_at ? formatDateTime(new Date(content.updated_at), DATE_FORMATS.ISO_DATE_STRING) : '-'),
  closed_at: ({ content }) => (content.closed_at ? formatDateTime(new Date(content.closed_at), DATE_FORMATS.ISO_DATE_STRING) : '-'),
  pbi_account_id: ({ content }) => content.pbi_account_id || '-',
  support_lead: ({ content }) => content.support_lead || '-',
  owners: ({ content }) => (content.owners.length > 0 ? content.owners[0].name : '-'),
  group_number: ({ content }) => content.group_number || '-',
  role: ({ content }) => getRole(content),
  default: ({ content, type }) => content[type],
  supportTicketId: ({ content, onSelect }) => (
    <AdasLink underline='none' className='cursor-pointer' onClick={() => onSelect(content)}>
      {content.ticket_number}
    </AdasLink>
  ),
  supportTicketStatus: ({ content }) => (
    <span className='cursor-pointer'>
      <SupportStatusInfo value={content.status} type='VIEW' />
    </span>
  ),
  is_scheduler_enabled: ({ content }) => (content.is_scheduler_enabled ? 'Active' : 'Inactive'),
  is_pgw_enabled: ({ content }) => (content.is_pgw_enabled ? 'Active' : 'Inactive'),
  is_sf_enabled: ({ content }) => (content.is_sf_enabled ? 'Active' : 'Inactive'),
  is_hanover_enabled: ({ content }) => (content.is_hanover_enabled ? 'Active' : 'Inactive'),
  is_allstate_enabled: ({ content }) => (content.is_allstate_enabled ? 'Active' : 'Inactive'),
  is_geico_enabled: ({ content }) => (content.is_geico_enabled ? 'Active' : 'Inactive'),
  is_bearriver_enabled: ({ content }) => (content.is_bearriver_enabled ? 'Active' : 'Inactive'),
  jobTitle: ({ content }) => (content.jobTitle ? content.jobTitle : <AdasTypography className='pl-[3%]'>-</AdasTypography>),
  rebate_percent: ({ content }) =>
    content.rebate_percent !== null && content.rebate_percent !== undefined ? `${content.rebate_percent}%` : '-',
  auto_apply_rebate: ({ content }) => (content.auto_apply_rebate ? 'Enabled' : 'Disabled'),
  ccc_client_id: ({ content }) => content.ccc_client_id || '-',
  // Explicit pencil edit affordance — opt in per column via `renderer: 'editAction'`.
  editAction: ({ content, onSelect }) => (
    <button
      type='button'
      onClick={(e) => {
        e.stopPropagation()
        onSelect(content, 'edit')
      }}
      aria-label='Edit'
      className='inline-flex h-7 w-7 items-center justify-center rounded-full text-[#475569] transition-colors hover:bg-[#EBEDF0] hover:text-[#1F6FEB]'
    >
      <Pencil className='h-3.5 w-3.5' />
    </button>
  )
}

export const AdasTable = ({
  containerProps,
  tableProps,
  columns,
  sortField,
  sortDirection,
  onSort,
  headerStyle,
  data,
  onSelect,
  tableRowProps,
  gradientHeader = false,
  sx
}) => (
  <div className={cn('w-full overflow-auto bg-card rounded-card', containerProps?.className)}>
    <Table {...tableProps}>
      <TableHeader>
        <TableRow
          className={cn(
            gradientHeader
              ? 'bg-gradient-to-r from-[#2C3D4D] to-[#1F2D3D] hover:from-[#2C3D4D] hover:to-[#1F2D3D]'
              : 'bg-custom-tableHeader hover:bg-custom-tableHeader'
          )}
        >
          {columns.map((column) => (
            <TableHead
              key={column.id}
              style={{ minWidth: column.minWidth, ...headerStyle }}
              className={cn(
                'text-table-header-foreground font-semibold uppercase select-none',
                gradientHeader ? 'text-[12px] tracking-wide h-11 px-4' : 'text-[11px] h-8 px-3',
                column.sortable !== false && 'cursor-pointer',
                column.align === 'center' && 'text-center',
                column.align === 'right' && 'text-right'
              )}
              onClick={column.sortable === false ? undefined : () => onSort(column.id)}
            >
              {column.label}
              {sortField === column.id && <SortIndicator direction={sortDirection} />}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((content, index) => (
          <TableRow
            key={index}
            className='h-[56px] cursor-pointer border-b border-[#EBEDF0] even:bg-[#F8FAFC] hover:bg-[#F0F4FF]'
            {...tableRowProps}
          >
            {columns.map((column) => (
              <TableCell
                key={column.id}
                className={cn(
                  'px-4 py-2 align-middle text-[13px] text-[#0A1628] max-w-0 overflow-hidden text-ellipsis whitespace-nowrap',
                  column.id === 'is_active' && 'text-center',
                  column.align === 'center' && 'text-center',
                  column.align === 'right' && 'text-right'
                )}
              >
                <span style={content[`${column.id}Style`]}>
                  {(renderers[column.renderer] || renderers[column.id] || renderers.default)({ content, onSelect, type: column.id })}
                </span>
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
)

AdasTable.propTypes = {
  containerProps: PropTypes.shape({ className: PropTypes.string }),
  tableProps: PropTypes.object,
  columns: PropTypes.array.isRequired,
  sortField: PropTypes.string.isRequired,
  sortDirection: PropTypes.string.isRequired,
  onSort: PropTypes.func.isRequired,
  headerStyle: PropTypes.object,
  data: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
  tableRowProps: PropTypes.object,
  gradientHeader: PropTypes.bool
}
